/**
 * @module @eoln/graphql-client/types
 * @description Shared types for GraphQL client implementations
 */

/**
 * Configuration for retry behavior.
 * When provided to a client, enables automatic retry with exponential backoff.
 */
export interface RetryConfig {
  /** Maximum number of retry attempts (default: 3) */
  maxAttempts?: number;
  /** Initial delay in milliseconds before first retry (default: 100) */
  initialDelayMs?: number;
  /** Maximum delay in milliseconds between retries (default: 2000) */
  maxDelayMs?: number;
  /** Multiplier for exponential backoff (default: 2) */
  backoffMultiplier?: number;
  /** Whether to add random jitter to delays (default: true) */
  jitter?: boolean;
}

/**
 * Configuration options for GraphQL clients.
 * All SDK clients should accept these options for consistency.
 */
export interface GraphQLClientOptions {
  /** URL of the GraphQL API endpoint */
  endpoint: string;
  /** API key for authentication (sent as x-api-key header) */
  apiKey?: string;
  /** Additional headers to include in requests */
  headers?: Record<string, string>;
  /**
   * Retry configuration for transient failures.
   * Set to `false` to disable retries entirely.
   * Default: enabled with standard configuration.
   */
  retry?: RetryConfig | false;
  /**
   * Per-attempt upstream timeout in milliseconds. NO DEFAULT, deliberately.
   *
   * A blanket default is an antipattern here: 10s aborted legitimate KSeF
   * submissions mid-flight, and raising it to 60s was a guess that bounded
   * nothing real while hiding whatever made one request slow. Set this only
   * from a measured p99 for the calls a given client makes. A timed-out
   * attempt surfaces as `RequestTimeoutError` and is never retried, because
   * the upstream may still be executing the request.
   */
  timeoutMs?: number;
}

/**
 * Standard GraphQL error structure returned by servers.
 */
export interface GraphQLError {
  /** Human-readable error message */
  message: string;
  /** Additional error metadata */
  extensions?: {
    /** Error code (e.g., UNAUTHENTICATED, FORBIDDEN) */
    code?: string;
    /** Required scopes for authorization errors */
    requiredScopes?: string[];
    /** Field-specific errors for validation failures */
    fieldErrors?: Record<string, string>;
  };
}

/**
 * Generic GraphQL response wrapper.
 * All GraphQL responses follow this structure.
 */
export interface GraphQLResponse<T = unknown> {
  /** Response data (present on success) */
  data?: T;
  /** Array of errors (present on failure) */
  errors?: GraphQLError[];
}

/**
 * Default retry configuration used by all SDK clients.
 * Provides consistent retry behavior across all services.
 */
export const DEFAULT_RETRY_CONFIG: Required<RetryConfig> = {
  maxAttempts: 3,
  initialDelayMs: 100,
  maxDelayMs: 2000,
  backoffMultiplier: 2,
  jitter: true,
};
